import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const hasImageComparisonResult = (image, item) => Boolean(
  item?.manualPreviewUrl || image?.smartCropUrl,
);

const JianyingImageCard = ({
  assignmentLabel = '',
  copyDisabled = false,
  disabled = false,
  entry,
  index,
  isSelected = false,
  onCopy = () => {},
  onCrop = () => {},
  onPreview = () => {},
  onRemove = () => {},
  onSelect = () => {},
  onSmartCrop = () => {},
  onSourceChange = () => {},
  showSequenceControls = false,
  smartCropDisabled = false,
  sortable = false,
  suppressDropAnimation = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.sortId || entry.id, disabled: disabled || !sortable });
  const image = entry?.image;
  const item = entry?.item;
  const unavailable = entry?.state === 'unavailable';
  const manualUrl = item?.manualPreviewUrl || '';
  const smartUrl = image?.smartCropUrl || '';
  const originalUrl = image?.originalUrl || image?.url || '';
  const hasResult = hasImageComparisonResult(image, item);
  const requestedSource = item?.source || (hasResult ? 'smart_crop' : 'original');
  const source = requestedSource === 'smart_crop' && !hasResult
    ? 'original'
    : requestedSource;
  const resultSource = manualUrl ? 'manual' : 'smart_crop';
  const cropUrl = manualUrl || smartUrl;
  const resultLabel = manualUrl ? '手动裁剪' : '自动裁剪';
  const smartCropProcessing = image?.smartCropStatus === 'processing';
  const selectSource = (nextSource) => {
    if (isSelected) {
      onSourceChange(nextSource);
      return;
    }
    onSelect(image, nextSource);
  };
  const displayIndex = Number.isInteger(index)
    ? String(index + 1).padStart(2, '0')
    : '—';

  return (
    <article
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: suppressDropAnimation ? 'none' : transition,
      }}
      className={`jianying-group-image-card is-${entry?.state || 'unselected'} ${
        hasResult ? 'has-comparison' : ''
      } ${disabled || unavailable ? 'is-disabled' : ''} ${isDragging ? 'is-dragging' : ''}`}
    >
      <div className="jianying-group-image-card__header">
        <div className="jianying-group-image-card__title">
          <span
            className={assignmentLabel ? 'is-assignment' : ''}
            aria-hidden={!assignmentLabel && !Number.isInteger(index)}
            title={assignmentLabel || undefined}
          >
            {assignmentLabel || displayIndex}
          </span>
          <strong>{image?.name || item?.image_id || ''}</strong>
        </div>
          <button
            type="button"
            className="jianying-group-image-card__remove"
            disabled={disabled}
            onClick={onRemove}
            aria-label={`取消选中第 ${Number(index) + 1} 张图片`}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
            <span className="jianying-group-image-card__remove-tooltip" role="tooltip">取消选中</span>
          </button>
      </div>

      <div className={`jianying-group-image-card__compare ${hasResult ? '' : 'is-single'}`}>
        <div className="jianying-group-image-card__choice-option">
          <button
            type="button"
            className={`jianying-group-image-card__choice is-original ${hasResult ? '' : 'is-only'} ${
              isSelected && source === 'original' ? 'is-active' : ''
            }`}
            disabled={disabled || unavailable}
            onClick={() => selectSource('original')}
            aria-pressed={isSelected && source === 'original'}
          >
            <span className="jianying-group-image-card__choice-check" aria-hidden="true">
              <i className="bi bi-check-lg" />
            </span>
            <span className="jianying-group-image-card__choice-preview">
              <img src={originalUrl} alt={`${image?.name || ''} 原图`} />
            </span>
            <span className="jianying-group-image-card__choice-label">
              <strong>原图</strong>
            </span>
          </button>
          <button
            type="button"
            className="jianying-group-image-card__choice-preview-action"
            disabled={!originalUrl}
            onClick={() => onPreview({
              alt: `${image?.name || ''} 原图`,
              source: 'original',
              src: originalUrl,
              title: `${image?.name || '图片'} · 原图`,
            })}
            aria-label={`放大预览 ${image?.name || '图片'} 原图`}
            title="放大预览"
          >
            <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
          </button>
        </div>
        {hasResult ? (
          <div className="jianying-group-image-card__choice-option">
            <button
              type="button"
              className={`jianying-group-image-card__choice is-result ${
                isSelected && source !== 'original' ? 'is-active' : ''
              }`}
              disabled={disabled || unavailable}
              onClick={() => selectSource(resultSource)}
              aria-pressed={isSelected && source !== 'original'}
            >
              <span className="jianying-group-image-card__choice-check" aria-hidden="true">
                <i className="bi bi-check-lg" />
              </span>
              <span className="jianying-group-image-card__choice-preview">
                <img src={cropUrl} alt={`${image?.name || ''} 裁剪结果`} />
              </span>
              <span className="jianying-group-image-card__choice-label">
                <strong>{resultLabel}</strong>
              </span>
            </button>
            <button
              type="button"
              className="jianying-group-image-card__choice-preview-action"
              disabled={!cropUrl}
              onClick={() => onPreview({
                alt: `${image?.name || ''} ${resultLabel}`,
                source: resultSource,
                src: cropUrl,
                title: `${image?.name || '图片'} · ${resultLabel}`,
              })}
              aria-label={`放大预览 ${image?.name || '图片'} ${resultLabel}`}
              title="放大预览"
            >
              <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      {isSelected && showSequenceControls ? (
        <div className="jianying-group-image-card__footer">
          <button
            type="button"
            className="jianying-group-image-card__drag"
            disabled={disabled}
            aria-label={`拖动第 ${Number(index) + 1} 张图片调整顺序`}
            title="拖动排序"
            {...attributes}
            {...listeners}
          >
            <i className="bi bi-grip-vertical" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="jianying-group-image-card__copy"
            disabled={disabled || copyDisabled || !image}
            onClick={onCopy}
            aria-label={`复制第 ${Number(index) + 1} 张图片到下一个空槽位`}
            title="复制"
          >
            <i className="bi bi-copy" aria-hidden="true" />
            <span>复制</span>
          </button>
          <div className="jianying-group-image-card__header-actions">
            <button
              type="button"
              className="jianying-group-image-card__smart-crop"
              disabled={disabled || smartCropDisabled || !image || smartCropProcessing}
              onClick={() => onSmartCrop(image)}
            >
              {smartCropProcessing ? '处理中…' : '自动裁剪'}
            </button>
            <button
              type="button"
              className="jianying-group-image-card__crop"
              disabled={disabled || !image}
              onClick={onCrop}
            >
              手动裁剪
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
};

export default JianyingImageCard;
